import * as React from "react";
// import { Count, Category, Query } from "./index";
import { useTranslation } from 'react-i18next';
import Field from "components/field";
import { Con, List, ListCategory, AmountRange, BalanceRange, Props } from "./style-and-types";


export function PaymentFilter({
  query,
  count,
  handleStatusClick,
  selected,
  search
}: Props) {
  const { t } = useTranslation();
  const tBase = "views.payments.paymentFilter";
  const tr = React.useCallback((key: string) => t(`${tBase}.${key}`), [t, tBase]);

  const [amountMin, setAmountMin] = React.useState("");
  const [amountMax, setAmountMax] = React.useState("");
  const [balanceMin, setBalanceMin] = React.useState("");
  const [balanceMax, setBalanceMax] = React.useState("");
  const [beginDate, setBeginDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState("All");
  
  // Use ref to avoid infinite re-renders
  const searchRef = React.useRef(search);
  searchRef.current = search;

  // Date validation: ensure end date is not before begin date
  const handleEndDateChange = React.useCallback((newEndDate: string) => {
    setEndDate(newEndDate);
    if (beginDate && newEndDate && newEndDate < beginDate) {
      setBeginDate(newEndDate);
    }
  }, [beginDate]);

  React.useEffect(() => {
    setAmountMin(query?.amountMin?.[0] || "");
    setAmountMax(query?.amountMax?.[0] || "");
    setBalanceMin(query?.balanceMin?.[0] || "");
    setBalanceMax(query?.balanceMax?.[0] || "");
    setBeginDate(query?.beginDate?.[0] || "");
    setEndDate(query?.endDate?.[0] || "");
    setSelectedPaymentMethod(query?.paymentMethod?.[0] || "All");
  }, [query]);

  // Auto-trigger search when any filter changes (only when the resulting params actually differ to avoid rapid re-runs)
  React.useEffect(() => {
    const filters: any = { amountMin, amountMax, balanceMin, balanceMax };

    if (beginDate) {
      filters.beginDate = [beginDate];
    } else {
      filters.beginDate = [];
    }
    if (endDate) {
      filters.endDate = [endDate];
    } else {
      filters.endDate = [];
    }
    if (selectedPaymentMethod && selectedPaymentMethod !== "All") {
      filters.paymentMethod = [selectedPaymentMethod];
    } else {
      // Clear paymentMethod so "All Payment Methods" triggers a requery without the filter
      filters.paymentMethod = undefined;
    }

    // Skip update if URL already matches our filter state (prevents feedback loop)
    const cur = query || {};
    const currentPaymentMethod = cur.paymentMethod?.[0];
    const expectedPaymentMethod = selectedPaymentMethod !== "All" ? selectedPaymentMethod : undefined;
    if (
      (currentPaymentMethod ?? "All") === (expectedPaymentMethod ?? "All") &&
      String(cur.amountMin?.[0] ?? "") === String(amountMin ?? "") &&
      String(cur.amountMax?.[0] ?? "") === String(amountMax ?? "") &&
      String(cur.balanceMin?.[0] ?? "") === String(balanceMin ?? "") &&
      String(cur.balanceMax?.[0] ?? "") === String(balanceMax ?? "") &&
      String(cur.beginDate?.[0] ?? "") === String(beginDate ?? "") &&
      String(cur.endDate?.[0] ?? "") === String(endDate ?? "")
    ) {
      return;
    }
    searchRef.current.update({ ...searchRef.current.search, ...filters, page: 1 });
  }, [query, selectedPaymentMethod, amountMin, amountMax, balanceMin, balanceMax, beginDate, endDate]);

  const populateStatus = React.useMemo(() => {
    const statuses = ["Voided", "Deposit", "Payment"];
    return statuses.map((status) => {
      const findCount = count?.find((count) => count.status === status) || { count: 0 };
  
      return (
        <List
          key={status}
          clicked={status === selected}
          onClick={() => {
            handleStatusClick(status);
          }}
        >
          <div>{tr(status)}</div>
          <div>{findCount.count}</div>
        </List>
      );
    });
  }, [count, selected, handleStatusClick, tr]);
  
  

  return (
    <Con className="border-r">
      <ListCategory>
        <p>
          {tr("Status")}
        </p>
        <p onClick={() => handleStatusClick("showAll")} style={{ color: "#FF4F00", cursor: "pointer" }}>
          {tr("Show All")}
        </p>     
      </ListCategory>
      {populateStatus}

    <p style={{ textAlign: "center" }}>
      {tr("Amount Range")}
    </p>

    {/* Min and Max fields */}
    <AmountRange>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Min")}
        // className={errState ? styles.inputFailed : styles.inputNormal}
        value={amountMin}
        setValue={setAmountMin}
        // onBlur={}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
      <p style={{ margin: "0 0.5rem", fontSize: "1.2rem", textAlign: "center" }}>-</p>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Max")}
        // className={errState ? styles.inputFailed : styles.inputNormal}
        value={amountMax}
        setValue={setAmountMax}
        style={{ width: "5rem" }}
        // onBlur={}
        advancedNumericalInputHandling
        allowLeaders
      />
    </AmountRange>

    <p style={{ textAlign: "center" }}>
      {tr("Balance Range")}
    </p>

    <BalanceRange>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Min")}
        // className={errState ? styles.inputFailed : styles.inputNormal}
        value={balanceMin}
        setValue={setBalanceMin}
        // onBlur={}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
      <p style={{ margin: "0 0.5rem", fontSize: "1.2rem", textAlign: "center" }}>-</p>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Max")}
        // className={errState ? styles.inputFailed : styles.inputNormal}
        value={balanceMax}
        setValue={setBalanceMax}
        style={{ width: "5rem" }}
        // onBlur={}
        advancedNumericalInputHandling
        allowLeaders
      />
    </BalanceRange>


    <p style={{ textAlign: "center", marginTop: "1rem" }}>
      {tr("Entry Date Range")}
    </p>

    <div style={{ margin: "0.5rem 1rem" }}>
      {/* Begin date */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <label style={{ fontSize: "0.9rem", fontWeight: "500" }}>{tr("Begin Date")}:</label>
        <input
          type="date"
          onChange={(e) => setBeginDate(e.target.value)}
          value={beginDate || ""}
          style={{ 
            fontSize: "0.8rem",
            padding: "0.25rem",
            borderRadius: "4px",
            border: "1px solid #d6dbdf",
            backgroundColor: "white"
          }}
        />
      </div>
      
      {/* End date */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <label style={{ fontSize: "0.9rem", fontWeight: "500" }}>{tr("End Date")}:</label>
        <input
          type="date"
          onChange={(e) => handleEndDateChange(e.target.value)}
          value={endDate || ""}
          style={{ 
            fontSize: "0.8rem",
            padding: "0.25rem",
            borderRadius: "4px",
            border: "1px solid #d6dbdf",
            backgroundColor: "white"
          }}
        />
      </div>
    </div>
          

    <p style={{ textAlign: "center", marginTop: "1rem" }}>
      {tr("Payment Method")}
    </p>

    <div style={{ margin: "0.5rem 1rem" }}>
      <select
        value={selectedPaymentMethod || "All"}
        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
        style={{
          width: "100%",
          padding: "0.5rem",
          borderRadius: "4px",
          border: "1px solid #d6dbdf",
          backgroundColor: "white",
          fontSize: "0.9rem"
        }}
      >
        <option value="All">{tr("All Payment Methods")}</option>
        {/* <option value="StripeACH">{tr("Stripe (ACH)")}</option> */}
        <option value="StripeCard">{tr("Stripe (Card)")}</option>
        <option value="ManualCash">{tr("Manual (Cash)")}</option>
        <option value="ManualCheck">{tr("Manual (Check)")}</option>
        <option value="ManualDonation">{tr("Manual (Donation)")}</option>
        <option value="ManualWriteOff">{tr("Manual (Write-Off)")}</option>
        <option value="ManualFinancing">{tr("Manual (Financing)")}</option>
        <option value="ManualUSBankAccount">{tr("Manual (US Bank Account)")}</option>
        <option value="ManualOther">{tr("Manual (Other)")}</option>
      </select>
    </div>


    </Con>
  );
}

export default React.memo(PaymentFilter);
